<template>
  <div class="flex min-h-full w-full flex-col gap-6" @click="onCodeCopyClick($event)">
    <div v-if="!messages.length && !streaming && !activeTool"
      class="flex min-h-full flex-1 flex-col items-center justify-center px-4 py-10 text-center">
      <slot name="empty">
        <div
          class="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-iris-500 to-iris-700 text-white shadow-lg shadow-iris-500/30">
          <Icon icon="tabler:robot" class="size-7" />
        </div>
        <h2 class="mt-4 text-2xl font-attention font-bold text-charcoal-900 dark:text-white">{{ emptyTitle }}</h2>
        <p class="mt-2 max-w-lg text-sm text-charcoal-500 dark:text-charcoal-400">{{ emptyDescription }}</p>

        <div v-if="suggestedPrompts.length"
          class="mt-7 grid w-full max-w-2xl grid-cols-1 gap-3 sm:grid-cols-2">
          <button v-for="prompt in suggestedPrompts" :key="prompt.label" type="button"
            class="group flex cursor-pointer items-center gap-3 rounded-2xl border border-charcoal-200 bg-white px-4 py-3 text-left shadow-sm transition-all duration-150 hover:-translate-y-0.5 hover:border-iris-300 hover:shadow-md dark:border-charcoal-700 dark:bg-charcoal-850 dark:hover:border-iris-600"
            :title="promptHint(prompt)"
            @click="emit('suggestion', prompt)">
            <div
              class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full transition-transform duration-150 group-hover:scale-110"
              :class="promptAccentClasses(prompt)">
              <Icon :icon="promptIcon(prompt)" class="size-4.5" />
            </div>
            <div class="min-w-0">
              <div class="truncate text-sm font-medium text-charcoal-800 dark:text-charcoal-100">{{ prompt.label }}</div>
              <div class="truncate text-xs text-charcoal-400 dark:text-charcoal-500">{{ promptHint(prompt) }}</div>
            </div>
          </button>
        </div>
      </slot>
    </div>

    <template v-for="(msg, i) in messages" :key="messageKey(msg, i)">
      <div v-if="msg.role === 'user'" class="group flex w-full justify-end prose prose-2xl">
        <div class="flex max-w-[92%] flex-row-reverse gap-3 sm:max-w-[80%]">
          <div
            class="mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-charcoal-200 text-charcoal-600 dark:bg-charcoal-700 dark:text-charcoal-200">
            <Icon icon="heroicons:user" class="size-4" />
          </div>
          <div class="flex min-w-0 flex-col items-end gap-2">
            <span
              class="px-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-charcoal-500 dark:text-charcoal-400">{{ userLabel }}</span>

            <div v-if="isEditing(i)" class="w-full min-w-[16rem]">
              <textarea v-model="editingText" ref="editTextarea" rows="3"
                class="agent-chat-scrollbar w-full resize-none rounded-2xl rounded-tr-sm border border-iris-300 bg-white px-4 py-3 text-[15px] leading-relaxed text-charcoal-900 outline-none focus:border-iris-400 focus:ring-2 focus:ring-iris-400 dark:border-iris-700 dark:bg-charcoal-850 dark:text-charcoal-100" />
              <div class="mt-2 flex items-center justify-end gap-2">
                <button
                  class="cursor-pointer rounded-lg px-3 py-1.5 text-xs font-medium text-charcoal-500 transition-colors hover:bg-charcoal-100 dark:text-charcoal-400 dark:hover:bg-charcoal-800"
                  @click="cancelEditing">Cancel</button>
                <button
                  class="cursor-pointer rounded-lg bg-iris-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-iris-500 disabled:cursor-not-allowed disabled:opacity-60"
                  :disabled="!editingText.trim()" @click="confirmEdit(i)">Save &amp; resend</button>
              </div>
            </div>
            <template v-else>
              <div
                class="agent-chat-copy rounded-2xl rounded-tr-sm bg-iris-600 px-5 py-3.5 text-[15px] leading-relaxed text-white shadow-sm"
                v-html="renderText(msg.content)" />
              <button v-if="!streaming" :class="ACTION_BUTTON_CLASS"
                @click="startEditing(msg, i)">
                <Icon icon="heroicons:pencil-square" class="size-3.5" />
                <span>Edit</span>
              </button>
            </template>
          </div>
        </div>
      </div>

      <div v-else-if="msg.role === 'assistant'" class="group flex w-full justify-start prose-2xl">
        <div class="flex max-w-[92%] gap-3 sm:max-w-[80%]">
          <div
            class="mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-iris-600 text-white shadow-sm dark:bg-iris-500">
            <Icon icon="tabler:robot" class="size-5" />
          </div>
          <div class="flex min-w-0 flex-col gap-2">
            <span
              class="px-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-charcoal-500 dark:text-charcoal-400">{{ assistantLabel }}</span>
            <div
              class="agent-chat-copy rounded-2xl rounded-tl-sm border bg-white px-5 py-3.5 text-[15px] leading-relaxed text-charcoal-900 shadow-sm dark:bg-charcoal-800 dark:text-charcoal-100"
              :class="msg.error ? 'border-peach-300 dark:border-peach-800' : 'border-charcoal-100 dark:border-charcoal-700'"
              v-html="renderText(msg.content)" />
            <div class="flex items-center gap-2 px-1">
              <button :class="ACTION_BUTTON_CLASS" @click="copyMessage(msg, i)">
                <Icon :icon="isCopied(msg, i) ? 'heroicons:check' : 'heroicons:clipboard'" class="size-3.5" />
                <span>{{ isCopied(msg, i) ? 'Copied' : 'Copy' }}</span>
              </button>
              <button v-if="!msg.error && !streaming" :class="ACTION_BUTTON_CLASS"
                @click="emit('regenerate', i)">
                <Icon icon="heroicons:arrow-path" class="size-3.5" />
                <span>Regenerate</span>
              </button>
              <button v-if="msg.error && i === messages.length - 1 && !streaming"
                class="flex cursor-pointer items-center gap-1 rounded-lg px-1.5 py-1 text-xs font-medium text-peach-600 transition-colors hover:bg-peach-50 dark:text-peach-500 dark:hover:bg-peach-950/30"
                @click="emit('retry')">
                <Icon icon="heroicons:arrow-path" class="size-3.5" />
                <span>Try again</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div v-else-if="msg.role === 'tool_call'" class="flex w-full justify-start">
        <div class="flex max-w-[92%] gap-3 sm:max-w-[80%]">
          <div
            class="mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-iris-600 text-white shadow-sm dark:bg-iris-500">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <rect x="3" y="11" width="18" height="10" rx="2"></rect>
              <circle cx="12" cy="5" r="2"></circle>
              <path d="M12 7v4"></path>
              <line x1="8" y1="16" x2="8" y2="16"></line>
              <line x1="16" y1="16" x2="16" y2="16"></line>
            </svg>
          </div>
          <div class="min-w-0 flex-1">
            <button
              class="flex w-full cursor-pointer items-center gap-3 overflow-hidden rounded-2xl border border-charcoal-200 bg-charcoal-50/90 px-4 py-3 text-left shadow-sm transition-colors duration-200 hover:bg-charcoal-100 dark:border-charcoal-700 dark:bg-charcoal-850 dark:hover:bg-charcoal-800"
              @click="toggleExpand(msg, i)">
              <Transition name="icon-fade" mode="out-in">
                <svg v-if="msg.status === 'done'" key="done" class="shrink-0 text-mantis-600 dark:text-mantis-500"
                  xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                  aria-hidden="true">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                  <polyline points="22 4 12 14.01 9 11.01"></polyline>
                </svg>
                <svg v-else key="running" class="shrink-0 animate-spin text-brand-orange"
                  xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                  aria-hidden="true">
                  <line x1="12" y1="2" x2="12" y2="6"></line>
                  <line x1="12" y1="18" x2="12" y2="22"></line>
                  <line x1="4.93" y1="4.93" x2="7.76" y2="7.76"></line>
                  <line x1="16.24" y1="16.24" x2="19.07" y2="19.07"></line>
                  <line x1="2" y1="12" x2="6" y2="12"></line>
                  <line x1="18" y1="12" x2="22" y2="12"></line>
                  <line x1="4.93" y1="19.07" x2="7.76" y2="16.24"></line>
                  <line x1="16.24" y1="7.76" x2="19.07" y2="4.93"></line>
                </svg>
              </Transition>
              <span class="min-w-0 flex-1">
                <span class="block truncate text-sm font-semibold text-charcoal-800 dark:text-charcoal-200">{{
                  msg.name }}</span>
                <span class="block truncate text-xs text-charcoal-500 dark:text-charcoal-400">{{
                  summarizeArgs(msg.args) || toolStatusText(msg) }}</span>
              </span>
              <span v-if="msg.status !== 'done'" class="shrink-0 text-xs text-charcoal-500 dark:text-charcoal-400">Running...</span>
              <svg :class="{ 'rotate-180': isExpanded(msg, i) }"
                class="shrink-0 text-charcoal-400 transition-transform duration-200 dark:text-charcoal-500"
                xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                aria-hidden="true">
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </button>

            <Transition name="expand">
              <div v-if="isExpanded(msg, i)"
                class="mt-2 overflow-hidden rounded-2xl border border-charcoal-200 bg-white shadow-sm dark:border-charcoal-700 dark:bg-charcoal-850">
                <div class="border-b border-charcoal-100 px-4 py-3 dark:border-charcoal-700">
                  <div
                    class="mb-1 text-[10px] font-bold uppercase tracking-[0.2em] text-charcoal-500 dark:text-charcoal-400">
                    Input</div>
                  <pre
                    class="overflow-x-auto whitespace-pre-wrap break-words text-xs leading-relaxed text-charcoal-700 dark:text-charcoal-300">{{ JSON.stringify(msg.args, null, 2) }}</pre>
                </div>
                <div class="px-4 py-3">
                  <div
                    class="mb-1 text-[10px] font-bold uppercase tracking-[0.2em] text-charcoal-500 dark:text-charcoal-400">
                    Result</div>
                  <pre
                    class="overflow-x-auto whitespace-pre-wrap break-words text-xs leading-relaxed text-charcoal-700 dark:text-charcoal-300">{{ formatResult(msg.result) }}</pre>
                </div>
              </div>
            </Transition>
          </div>
        </div>
      </div>
    </template>

    <div v-if="isThinking" class="flex w-full justify-start prose-2xl">
      <div class="flex max-w-[92%] gap-3 sm:max-w-[80%]">
        <div
          class="mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-iris-600 text-white shadow-sm dark:bg-iris-500">
          <Icon icon="tabler:robot" class="size-5" />
        </div>
        <div class="flex min-w-0 items-center">
          <span class="animate-pulse px-1 text-sm text-charcoal-500 dark:text-charcoal-400">{{ thinkingPhrase }}</span>
        </div>
      </div>
    </div>

    <div v-if="streaming && streamBuffer" class="flex w-full justify-start prose-2xl">
      <div class="flex max-w-[92%] gap-3 sm:max-w-[80%]">
        <div
          class="mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-iris-600 text-white shadow-sm dark:bg-iris-500">
          <Icon icon="tabler:robot" class="size-5" />
        </div>
        <div class="flex min-w-0 flex-col gap-2">
          <span
            class="px-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-charcoal-500 dark:text-charcoal-400">{{ streamingLabel }}</span>
          <div
            class="agent-chat-copy rounded-2xl rounded-tl-sm border border-charcoal-100 bg-white px-5 py-3.5 text-[15px] leading-relaxed text-charcoal-900 shadow-sm dark:border-charcoal-700 dark:bg-charcoal-800 dark:text-charcoal-100"
            v-html="streamHtml"></div>
        </div>
      </div>
    </div>

    <div v-if="activeTool" class="flex w-full justify-start">
      <div class="flex max-w-[92%] gap-3 sm:max-w-[80%]">
        <div
          class="mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-iris-600 text-white shadow-sm dark:bg-iris-500">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <rect x="3" y="11" width="18" height="10" rx="2"></rect>
            <circle cx="12" cy="5" r="2"></circle>
            <path d="M12 7v4"></path>
            <line x1="8" y1="16" x2="8" y2="16"></line>
            <line x1="16" y1="16" x2="16" y2="16"></line>
          </svg>
        </div>
        <div
          class="min-w-0 flex-1 rounded-2xl border border-charcoal-200 bg-charcoal-50/90 px-4 py-3 shadow-sm dark:border-charcoal-700 dark:bg-charcoal-850">
          <div class="flex items-center gap-3">
            <svg class="shrink-0 animate-spin text-brand-orange" xmlns="http://www.w3.org/2000/svg" width="16"
              height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
              stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <line x1="12" y1="2" x2="12" y2="6"></line>
              <line x1="12" y1="18" x2="12" y2="22"></line>
              <line x1="4.93" y1="4.93" x2="7.76" y2="7.76"></line>
              <line x1="16.24" y1="16.24" x2="19.07" y2="19.07"></line>
              <line x1="2" y1="12" x2="6" y2="12"></line>
              <line x1="18" y1="12" x2="22" y2="12"></line>
              <line x1="4.93" y1="19.07" x2="7.76" y2="16.24"></line>
              <line x1="16.24" y1="7.76" x2="19.07" y2="4.93"></line>
            </svg>
            <span class="min-w-0 flex-1">
              <span class="block truncate text-sm font-semibold text-charcoal-800 dark:text-charcoal-200">{{
                activeTool.name }}</span>
              <span class="block truncate text-xs text-charcoal-500 dark:text-charcoal-400">{{
                summarizeArgs(activeTool.args) || 'Running tool...' }}</span>
            </span>
            <span class="animate-pulse shrink-0 text-xs text-charcoal-500 dark:text-charcoal-400">Running...</span>
          </div>
        </div>
      </div>
    </div>

    <QuestionPrompt v-if="pendingQuestion" :question="pendingQuestion" :disabled="streaming"
      @answer="emit('answer', $event)" />

    <BoostPrompt v-if="pendingBoost" :request="pendingBoost" :disabled="streaming"
      @answer="emit('answer', $event)" />

    <ToolFormPrompt v-if="pendingToolPrompt" :config="TOOL_PROMPTS[pendingToolPrompt.key]" :disabled="streaming"
      @answer="emit('answer', $event)" />

    <!-- /task and /request render inline here too (ChatDialogShell's `inline` mode)
         instead of popping a Dialog over the whole chat, matching every other
         slash command's UX. -->
    <ModalTask v-if="pendingTask" inline :openModal="true" :taskToUpdate="null" @close-modal="emit('close-task')"
      @reset-modal="emit('close-task')" @submitTask="(task) => emit('submit-task', task)" />

    <FieldLensRequestWizard v-if="pendingFieldLensRequest" inline @close="emit('close-field-lens-request')"
      @submit="(payload) => emit('submit-field-lens-request', payload)" />
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import { marked } from 'marked'
import { Icon } from '@iconify/vue'
import QuestionPrompt from '@/components/Agent/QuestionPrompt.vue'
import BoostPrompt from '@/components/Agent/BoostPrompt.vue'
import ToolFormPrompt from '@/components/Agent/ToolFormPrompt.vue'
import { TOOL_PROMPTS } from '@/components/Agent/toolPrompts.js'
import { AGENT_ACCENT_CLASSES } from '@/components/Agent/agentAccentClasses.js'
import ModalTask from '@/components/Task/ModalTask.vue'
import FieldLensRequestWizard from '@/components/FieldLens/FieldLensRequestWizard.vue'
import { useConfirmDialog } from '@/composables/useConfirmDialog.js'
import { escapeHtml, onCodeCopyClick, renderCodeBlockWithCopyButton } from '@/composables/useCodeCopyButtons.js'

// Sources (Salesforce records, Intercom articles, or any other external page a
// tool result gave the model — see linkInstructions in controllers/
// conversations.js) are cited with a normal markdown link the model writes
// itself, e.g. "[Acme Corp](https://.../0016000000tsZlNAAU)". There is no
// separate citation/ref system on top of that: marked already turns a
// markdown link into an `<a>`; a small numbered icon badge is appended next
// to each one purely as a visual "this is a source" marker.
//
// Only once the message has finished streaming, though (see renderingStreaming
// below): while still streaming in, the set of links in the message keeps
// growing, so numbering them now would mean earlier numbers show up and
// resettle repeatedly before the message is done. Simpler and calmer to
// render links completely plainly while streaming and add the numbered
// badges in one pass, once, right as the finished message replaces the
// stream buffer in the template.
const LINK_ICON = '<svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path><polyline points="15 3 21 3 21 9"></polyline><line x1="10" y1="14" x2="21" y2="3"></line></svg>'

// Built via a throwaway DOM element rather than a hand-assembled HTML string:
// setting `.href`/`.title` as element properties can never break out of its
// own attribute no matter what characters an href/title contains, which a
// `<a href="${href}">` template string is one stray quote or bracket away from
// doing — that was previously corrupting the message into raw, half-parsed
// tag soup shown as visible text right in the chat.
function buildLink({ href, title, text, bareUrl, index }) {
  const a = document.createElement('a')
  a.href = href
  a.target = '_blank'
  a.rel = 'noopener noreferrer'
  if (title) a.title = title

  // Still streaming (see renderingStreaming) — a completely plain link, no
  // icon, no number.
  if (index == null) {
    a.innerHTML = text
    return a.outerHTML
  }

  const badge = `${LINK_ICON}<span class="link-icon-chip-index">${index}</span>`

  // A bare URL the model wrote as its own link text (e.g. it pasted the raw
  // URL instead of a description) has nothing worth keeping as visible text —
  // replace it outright instead of showing the badge next to a wall of URL.
  if (bareUrl) {
    if (!title) a.title = href
    a.className = 'link-icon-chip'
    a.innerHTML = badge
  } else {
    a.innerHTML = `${text} <span class="link-icon-chip">${badge}</span>`
  }

  return a.outerHTML
}

// Both reset at the top of every renderText call: numbering restarts per
// message instead of climbing forever across the whole conversation, and
// whether links get numbered at all depends on this specific render (see
// renderText).
let linkCounter = 0
let renderingStreaming = false

// Give every fenced code block its own hover "Copy" button instead of relying
// solely on the whole-message Copy action below. Registered once for the
// shared `marked` instance since `renderText` calls `marked.parse` on every
// render.
marked.use({
  renderer: {
    code({ text, lang }) {
      return renderCodeBlockWithCopyButton(escapeHtml(text), lang)
    },
    link({ href, title, tokens }) {
      // Never render a link for anything other than http(s) — an internal
      // chat link (mailto:, javascript:, or a bare fragment) is never a real
      // external source and should just read as plain text.
      if (!/^https?:\/\//i.test(href)) return this.parser.parseInline(tokens)

      const text = this.parser.parseInline(tokens)
      const bareUrl = text.trim() === href.trim()

      if (renderingStreaming) return buildLink({ href, title, text, bareUrl, index: null })

      linkCounter += 1
      return buildLink({ href, title, text, bareUrl, index: linkCounter })
    },
  },
})

const emit = defineEmits([
  'answer', 'retry', 'regenerate', 'edit', 'suggestion',
  'close-task', 'submit-task', 'close-field-lens-request', 'submit-field-lens-request',
])
const { confirm } = useConfirmDialog()

// Shared by the small Copy/Regenerate/Edit buttons under a message: hidden until
// the message is hovered or focused, then styled the same neutral way.
const ACTION_BUTTON_CLASS = 'flex cursor-pointer items-center gap-1 rounded-lg px-1.5 py-1 text-xs text-charcoal-400 opacity-0 transition-opacity duration-150 hover:bg-charcoal-100 hover:text-charcoal-600 group-hover:opacity-100 focus-visible:opacity-100 dark:text-charcoal-500 dark:hover:bg-charcoal-800 dark:hover:text-charcoal-300'

const props = defineProps({
  activeTool: { type: Object, default: null },
  pendingBoost: { type: Object, default: null },
  pendingFieldLensRequest: { type: Object, default: null },
  pendingQuestion: { type: Object, default: null },
  pendingTask: { type: Object, default: null },
  pendingToolPrompt: { type: Object, default: null },
  assistantLabel: { type: String, default: 'Helper Bot' },
  emptyDescription: {
    type: String,
    default: 'I can answer questions, call tools, and explain the steps I took as the conversation unfolds.',
  },
  emptyTitle: { type: String, default: 'How can I help you today?' },
  messages: { type: Array, default: () => [] },
  streamBuffer: { type: String, default: '' },
  streaming: { type: Boolean, default: false },
  streamingLabel: { type: String, default: 'Assistant' },
  // Empty-state quick-start chips. `label` is the chip text; provide exactly one
  // of `message` (sent immediately on click), `template` (a fill-in-the-blank
  // string, with [placeholder] tokens or a bare "@", dropped into the input for
  // editing instead of being sent), or `tool` (opens the same structured
  // bubble/composer as that tool's slash command — see promptHint/promptIcon
  // below and runToolCommand in AssistantChat.vue). e.g. { label: 'My open
  // POCs', message: 'What...' }, { label: 'Boost a request', template: 'Boost
  // the request for [account]...' }, or { label: 'Create follow-up actions',
  // tool: 'create_task' }. `icon` (an Iconify name) and `accent` (a key into
  // PROMPT_ACCENT_CLASSES below) are optional cosmetic overrides — omit either
  // to fall back to a generic one based on the chip's shape.
  suggestedPrompts: { type: Array, default: () => [] },
  userLabel: { type: String, default: 'You' },
})

// See the suggestedPrompts prop doc above for the three shapes a chip can take.
function promptHint(prompt) {
  if (prompt.tool) return 'Opens a quick form'
  if (prompt.template) return 'Fill in the blanks and send'
  return 'Send now'
}

// `prompt.icon` (see SUGGESTED_PROMPTS in AssistantChat.vue) is per-chip and
// cosmetic; these are just the fallback if a prompt doesn't bother setting one.
function promptIcon(prompt) {
  if (prompt.icon) return prompt.icon
  if (prompt.tool) return 'heroicons:clipboard-document-list'
  if (prompt.template) return 'heroicons:pencil-square'
  return 'heroicons:sparkles'
}

// Mirrors ToolsPopover.vue's CATEGORIES coloring (both pull from the shared
// AGENT_ACCENT_CLASSES map) so the same underlying tool reads as the same
// color in both the Capabilities popover and these chips.
function promptAccentClasses(prompt) {
  const key = prompt.accent ?? (prompt.tool ? 'mantis' : prompt.template ? 'iris' : 'sky')
  return AGENT_ACCENT_CLASSES[key] ?? AGENT_ACCENT_CLASSES.charcoal
}

// True for the gap between the user's message going out and the first streamed
// token coming back. Tool calls and interactive prompts already render their own
// "in progress" state, so the dots stay out of the way once one of those appears.
const isThinking = computed(() => {
  if (!props.streaming || props.streamBuffer) return false
  if (props.pendingQuestion || props.pendingBoost || props.pendingToolPrompt || props.pendingTask || props.pendingFieldLensRequest) return false

  const lastMessage = props.messages[props.messages.length - 1]
  return !(lastMessage?.role === 'tool_call' && lastMessage.status !== 'done')
})

// Cycles through short status phrases while isThinking is true, the same way
// Cursor/Copilot/Perplexity do, so the wait for a first token reads as "doing
// something" instead of a static/frozen spinner.
const THINKING_PHRASES = [
  'Thinking...',
  'Analyzing your request...',
  'Reviewing the conversation...',
  'Looking into it...',
  'Gathering context...',
  'Putting it together...',
  'Considering the options...',
  'Working on it...',
  'Almost there...',
]

const thinkingPhraseIndex = ref(0)
const thinkingPhrase = computed(() => THINKING_PHRASES[thinkingPhraseIndex.value % THINKING_PHRASES.length])
// A random delay each time (rather than a fixed setInterval) so the cadence
// doesn't read as mechanically even.
const THINKING_MIN_DELAY_MS = 600
const THINKING_MAX_DELAY_MS = 2500
let thinkingTimerId = null

function randomThinkingDelay() {
  return THINKING_MIN_DELAY_MS + Math.random() * (THINKING_MAX_DELAY_MS - THINKING_MIN_DELAY_MS)
}

function scheduleNextThinkingPhrase() {
  thinkingTimerId = setTimeout(() => {
    thinkingPhraseIndex.value += 1
    scheduleNextThinkingPhrase()
  }, randomThinkingDelay())
}

function startThinkingCycle() {
  if (thinkingTimerId != null) return
  thinkingPhraseIndex.value = 0
  scheduleNextThinkingPhrase()
}

function stopThinkingCycle() {
  if (thinkingTimerId == null) return
  clearTimeout(thinkingTimerId)
  thinkingTimerId = null
}

watch(isThinking, (thinking) => {
  if (thinking) {
    startThinkingCycle()
  } else {
    stopThinkingCycle()
  }
}, { immediate: true })

const expandedToolCalls = ref({})

watch(() => props.messages, () => {
  expandedToolCalls.value = {}
  cancelEditing()
})

// `streaming: true` is only ever passed for the live streamBuffer render below
// — it tells the `link()` renderer registered above to skip the numbered icon
// badge entirely and leave links plain until the message is finished, so the
// numbering never has to shift around mid-stream (see the comment above
// LINK_ICON). Once streaming ends, the parent swaps the live streamBuffer
// bubble out for the finished message (rendered below with no `streaming`
// option), which is where the numbered badges first appear, all at once.
function renderText(md, { streaming = false } = {}) {
  linkCounter = 0
  renderingStreaming = streaming
  return marked.parse(md ?? '')
}

// Deltas can arrive many times per frame. Re-parsing markdown on every single one
// is wasted work the browser can't even paint in between, so we coalesce updates
// to once per animation frame and only render whatever the buffer holds by then.
const streamHtml = ref('')
let streamRenderHandle = null

function cancelScheduledStreamRender() {
  if (streamRenderHandle == null) return
  cancelAnimationFrame(streamRenderHandle)
  streamRenderHandle = null
}

watch(() => props.streamBuffer, (value) => {
  if (!value) {
    cancelScheduledStreamRender()
    streamHtml.value = ''
    return
  }

  if (streamRenderHandle != null) return

  streamRenderHandle = requestAnimationFrame(() => {
    streamRenderHandle = null
    streamHtml.value = renderText(props.streamBuffer, { streaming: true })
  })
}, { immediate: true })

onBeforeUnmount(() => {
  cancelScheduledStreamRender()
  stopThinkingCycle()
})

function summarizeArgs(args) {
  if (!args || !Object.keys(args).length) return ''
  return Object.entries(args)
    .map(([key, value]) => `${key}: ${typeof value === 'string' ? value : JSON.stringify(value)}`)
    .join('  ·  ')
}

function formatResult(result) {
  if (result == null) return '—'
  try {
    const parsed = typeof result === 'string' ? JSON.parse(result) : result
    return JSON.stringify(parsed, null, 2)
  } catch {
    return String(result)
  }
}

function toolStatusText(message) {
  return message.status === 'done' ? 'No input arguments' : 'Running tool...'
}

function toolMessageKey(message, index) {
  return message.toolCallId ?? `${message.name ?? 'tool'}-${index}`
}

function messageKey(message, index) {
  if (message.role === 'tool_call') {
    return `tool-${toolMessageKey(message, index)}`
  }

  return `${message.role}-${index}`
}

const copiedKey = ref(null)

function isCopied(message, index) {
  return copiedKey.value === messageKey(message, index)
}

async function copyMessage(message, index) {
  const key = messageKey(message, index)
  try {
    await navigator.clipboard.writeText(message.content ?? '')
    copiedKey.value = key
    setTimeout(() => {
      if (copiedKey.value === key) copiedKey.value = null
    }, 1500)
  } catch {
    // Clipboard access can be denied/unavailable; copy is a non-critical nice-to-have.
  }
}

const editingIndex = ref(null)
const editingText = ref('')
const editTextarea = ref(null)

function isEditing(index) {
  return editingIndex.value === index
}

function startEditing(message, index) {
  if (props.streaming) return

  editingIndex.value = index
  editingText.value = message.content ?? ''

  nextTick(() => editTextarea.value?.focus())
}

function cancelEditing() {
  editingIndex.value = null
  editingText.value = ''
}

// Editing a message discards everything the model said in response to it (and
// anything after that), so this confirms before resending — the same trade-off
// as regenerating, just paired with new text instead of the original.
async function confirmEdit(index) {
  const text = editingText.value.trim()
  if (!text) return

  const CONFIRM_LABEL = 'Discard and resend'
  const CANCEL_LABEL = 'Keep editing'

  const action = await confirm(
    'Saving this edit discards everything after it in this conversation, and the assistant responds to the new version instead.',
    { title: 'Edit message?', confirmLabel: CONFIRM_LABEL, cancelLabel: CANCEL_LABEL }
  )

  if (action !== CONFIRM_LABEL) return

  cancelEditing()
  emit('edit', { index, text })
}

function isExpanded(message, index) {
  return Boolean(expandedToolCalls.value[toolMessageKey(message, index)])
}

function toggleExpand(message, index) {
  if (message.status !== 'done') return

  const key = toolMessageKey(message, index)
  expandedToolCalls.value = {
    ...expandedToolCalls.value,
    [key]: !expandedToolCalls.value[key],
  }
}

</script>

<style>
.expand-enter-active,
.expand-leave-active {
  transition: max-height 0.25s ease, opacity 0.2s;
  overflow: hidden;
  max-height: 600px;
}

.expand-enter-from,
.expand-leave-to {
  max-height: 0;
  opacity: 0;
}

.icon-fade-enter-active,
.icon-fade-leave-active {
  transition: opacity 0.2s ease;
}

.icon-fade-enter-from,
.icon-fade-leave-to {
  opacity: 0;
}

.agent-chat-copy :first-child {
  margin-top: 0;
}

.agent-chat-copy :last-child {
  margin-bottom: 0;
}

.agent-chat-copy pre {
  overflow-x: auto;
  border-radius: 0.75rem;
  padding: 0.75rem 0.875rem;
  background: rgb(19 19 19 / 0.08);
}

.dark .agent-chat-copy pre {
  background: rgb(255 255 255 / 0.06);
}

.agent-chat-copy code {
  font-size: 0.875em;
}

.agent-chat-copy a {
  text-decoration: underline;
}

/* Used two ways: as the whole content of an `<a>` when the model's link text
   was just the bare URL itself (nothing worth showing), and as a `<span>`
   nested inside a normal `<a>` right after its real link text otherwise. */
.link-icon-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.15rem;
  height: 1.1rem;
  padding: 0 0.35rem;
  margin: 0 0.15rem;
  border-radius: 9999px;
  vertical-align: text-top;
  background: var(--color-iris-100);
  color: var(--color-iris-700);
  text-decoration: none;
  transition: background-color 0.15s ease;
}

.link-icon-chip-index {
  font-family: inherit;
  font-size: 0.6875rem;
  font-weight: 600;
  line-height: 1;
}

.agent-chat-copy a.link-icon-chip:hover,
.agent-chat-copy a:hover .link-icon-chip {
  background: var(--color-iris-200);
}

.dark .link-icon-chip {
  background: var(--color-iris-800);
  color: var(--color-iris-200);
}

.dark .agent-chat-copy a.link-icon-chip:hover,
.dark .agent-chat-copy a:hover .link-icon-chip {
  background: var(--color-iris-700);
}
</style>
